import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Check, Download, ExternalLink, ArrowRight, Loader2, Play, Calendar } from 'lucide-react';

const PRODUCTS = {
  book: {
    name: "Story Framework Formula",
    type: "ebook",
    downloadUrl: "/downloads/story-framework-formula.pdf", // Placeholder
    upsell: {
      product: "course",
      title: "Ready to Go Deeper?",
      description: "Get the complete AI for Creatives Course and learn how to implement these frameworks with cutting-edge AI tools.",
      price: 197,
      ctaText: "Get the Course"
    }
  },
  course_standard: {
    name: "AI for Creatives Course",
    type: "course",
    accessUrl: "https://school.ramongavea.com/login", // Placeholder
    upsell: {
      product: "audit",
      title: "Want Personalized Guidance?",
      description: "Book a 1-on-1 Strategy Audit to get custom recommendations for your specific IP.",
      price: 497,
      ctaText: "Book Strategy Audit"
    }
  },
  course_vip: {
    name: "AI for Creatives Course (VIP)",
    type: "course",
    accessUrl: "https://school.ramongavea.com/login", // Placeholder
    includesAudit: true
  },
  audit: {
    name: "60 Minute IP Strategy Audit",
    type: "audit",
    calendarUrl: "https://calendly.com/ramon-govea/60-minute-ip-strategy-audit"
  }
};

const SuccessPage = () => {
  const { productId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  
  const [paymentStatus, setPaymentStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);
  const pollAttemptsRef = React.useRef(0);

  const product = PRODUCTS[productId];

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setPaymentStatus('error');
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (pollAttemptsRef.current >= maxAttempts) {
      setPaymentStatus('timeout');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/checkout/status/${sessionId}`);
      
      if (!response.ok) {
        throw new Error('Failed to check payment status');
      }

      const data = await response.json();
      setPaymentData(data);

      if (data.payment_status === 'paid') {
        setPaymentStatus('success');
        return;
      } else if (data.status === 'expired') {
        setPaymentStatus('expired');
        return;
      }

      // Continue polling
      pollAttemptsRef.current += 1;
      if (pollAttemptsRef.current < maxAttempts) {
        setTimeout(pollPaymentStatus, pollInterval);
      } else {
        setPaymentStatus('timeout');
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      pollAttemptsRef.current += 1;
      if (pollAttemptsRef.current < maxAttempts) {
        setTimeout(pollPaymentStatus, pollInterval);
      } else {
        setPaymentStatus('error');
      }
    }
  };

  if (!product) {
    return (
      <div className="min-h-screen bg-[#0a0a12] text-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-serif mb-4">Product not found</h1>
          <button
            onClick={() => navigate('/ai-for-creatives')}
            className="text-[#c9a962] underline"
          >
            Return to quiz
          </button>
        </div>
      </div>
    );
  }

  // Loading state
  if (paymentStatus === 'checking') {
    return (
      <div className="min-h-screen bg-[#0a0a12] text-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={48} className="animate-spin text-[#c9a962] mx-auto mb-6" />
          <h2 className="text-2xl font-serif mb-2">Confirming Your Payment</h2>
          <p className="text-gray-400">Please wait while we verify your purchase...</p>
        </div>
      </div>
    );
  }

  // Error states
  if (paymentStatus === 'error' || paymentStatus === 'expired' || paymentStatus === 'timeout') {
    return (
      <div className="min-h-screen bg-[#0a0a12] text-white flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
            <span className="text-4xl">!</span>
          </div>
          <h2 className="text-2xl font-serif mb-4">
            {paymentStatus === 'expired' ? 'Payment Session Expired' : 'Payment Verification Issue'}
          </h2>
          <p className="text-gray-400 mb-8">
            {paymentStatus === 'expired' 
              ? 'Your payment session has expired. Please try again.'
              : 'We had trouble verifying your payment. If you completed the purchase, please check your email for confirmation or contact support.'}
          </p>
          <div className="space-y-4">
            <button
              onClick={() => navigate(`/ai-for-creatives/checkout/${productId}`)}
              className="w-full bg-gradient-to-r from-[#c9a962] to-[#d4b978] text-black py-3 font-semibold rounded-lg"
            >
              Try Again
            </button>
            <a
              href="mailto:support@ramongavea.com"
              className="block text-[#c9a962] underline"
            >
              Contact Support
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  return (
    <div className="min-h-screen bg-[#0a0a12] text-white">
      <div className="max-w-3xl mx-auto px-6 py-16">
        {/* Success Header */}
        <div className="text-center mb-12">
          <div className="w-20 h-20 rounded-full bg-gradient-to-r from-[#c9a962] to-[#d4b978] flex items-center justify-center mx-auto mb-6">
            <Check size={40} className="text-black" />
          </div>
          <h1 className="text-3xl md:text-4xl font-serif mb-4">Thank You for Your Purchase!</h1>
          <p className="text-gray-400">
            Your order for <span className="text-white font-semibold">{product.name}</span> is confirmed.
          </p>
        </div>

        {/* Product Delivery */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-8 mb-8">
          <h2 className="text-xl font-semibold mb-6">Access Your Purchase</h2>
          
          {/* E-book delivery */}
          {product.type === 'ebook' && (
            <div className="space-y-4">
              <p className="text-gray-300">
                Your e-book is ready for download. Click the button below to get your copy.
              </p>
              <a
                href={product.downloadUrl}
                download
                className="inline-flex items-center gap-3 bg-gradient-to-r from-[#c9a962] to-[#d4b978] text-black px-8 py-4 font-semibold rounded-lg hover:shadow-lg hover:shadow-[#c9a962]/30 transition-all"
                data-testid="download-button"
              >
                <Download size={20} />
                Download Story Framework Formula (PDF)
              </a>
              <p className="text-sm text-gray-500">
                A copy has also been sent to your email for safekeeping.
              </p>
            </div>
          )}

          {/* Course delivery */}
          {product.type === 'course' && (
            <div className="space-y-4">
              <p className="text-gray-300">
                Your course access is ready. Click below to create your account and start learning.
              </p>
              <a
                href={product.accessUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 bg-gradient-to-r from-[#c9a962] to-[#d4b978] text-black px-8 py-4 font-semibold rounded-lg hover:shadow-lg hover:shadow-[#c9a962]/30 transition-all"
                data-testid="access-course-button"
              >
                <Play size={20} />
                Access Your Course
                <ExternalLink size={16} />
              </a>
              <p className="text-sm text-gray-500">
                Login instructions have been sent to your email.
              </p>

              {/* VIP includes audit */}
              {product.includesAudit && (
                <div className="mt-6 pt-6 border-t border-white/10">
                  <div className="flex items-center gap-2 mb-3">
                    <Calendar size={20} className="text-[#c9a962]" />
                    <h3 className="font-semibold">Your VIP Strategy Audit</h3>
                  </div>
                  <p className="text-gray-300 text-sm mb-4">
                    As a VIP member, you also get a 30-minute Strategy Audit call with Ramon.
                  </p>
                  <a
                    href="https://calendly.com/ramon-govea/30-minute-vip-strategy-audit"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 border border-[#c9a962] text-[#c9a962] px-6 py-3 rounded-lg hover:bg-[#c9a962]/10 transition-colors"
                  >
                    <Calendar size={18} />
                    Schedule Your VIP Call
                  </a>
                </div>
              )}
            </div>
          )}

          {/* Audit delivery */}
          {product.type === 'audit' && (
            <div className="space-y-4">
              <p className="text-gray-300">
                Your Strategy Audit is confirmed! You should have received a calendar invite with the meeting details.
              </p>
              <div className="bg-[#c9a962]/10 border border-[#c9a962]/30 rounded-lg p-4">
                <h3 className="font-semibold mb-2">What to Prepare</h3>
                <ul className="text-sm text-gray-300 space-y-2">
                  <li>• A brief summary of your IP/project (1-2 paragraphs)</li>
                  <li>• Any existing materials (pitch deck, treatment, visuals)</li>
                  <li>• Your top 3 questions or challenges</li>
                  <li>• Your goals for the next 6-12 months</li>
                </ul>
              </div>
              <p className="text-sm text-gray-500">
                Check your email for prep materials and the meeting link.
              </p>
            </div>
          )}
        </div>

        {/* What's Next */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-8 mb-8">
          <h2 className="text-xl font-semibold mb-4">What Happens Next</h2>
          <ul className="space-y-3">
            {product.type === 'ebook' && (
              <>
                <li className="flex items-start gap-3">
                  <Check size={18} className="text-[#c9a962] mt-0.5" />
                  <span className="text-gray-300">Download and read through the framework (takes ~2 hours)</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check size={18} className="text-[#c9a962] mt-0.5" />
                  <span className="text-gray-300">Use the templates to map out your IP's franchise potential</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check size={18} className="text-[#c9a962] mt-0.5" />
                  <span className="text-gray-300">Apply the 5-part framework to identify expansion opportunities</span>
                </li>
              </>
            )}
            {product.type === 'course' && (
              <>
                <li className="flex items-start gap-3">
                  <Check size={18} className="text-[#c9a962] mt-0.5" />
                  <span className="text-gray-300">Create your account and explore the course dashboard</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check size={18} className="text-[#c9a962] mt-0.5" />
                  <span className="text-gray-300">Start with Module 1: Foundations of AI-Native IP Development</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check size={18} className="text-[#c9a962] mt-0.5" />
                  <span className="text-gray-300">Join the private community to connect with other creators</span>
                </li>
              </>
            )}
            {product.type === 'audit' && (
              <>
                <li className="flex items-start gap-3">
                  <Check size={18} className="text-[#c9a962] mt-0.5" />
                  <span className="text-gray-300">Review the prep materials sent to your email</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check size={18} className="text-[#c9a962] mt-0.5" />
                  <span className="text-gray-300">Gather your IP materials for review during the call</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check size={18} className="text-[#c9a962] mt-0.5" />
                  <span className="text-gray-300">Join the call ready to dive deep into your franchise strategy</span>
                </li>
              </>
            )}
          </ul>
        </div>

        {/* Upsell */}
        {product.upsell && (
          <div className="border border-white/10 rounded-xl p-8 text-center">
            <p className="text-[#c9a962] uppercase tracking-widest text-sm mb-2">{product.upsell.title}</p>
            <p className="text-gray-300 mb-6">{product.upsell.description}</p>
            <button
              onClick={() => navigate(`/ai-for-creatives/checkout/${product.upsell.product}`)}
              className="inline-flex items-center gap-2 border border-[#c9a962] text-[#c9a962] px-8 py-3 rounded-lg font-semibold hover:bg-[#c9a962]/10 transition-colors"
              data-testid="upsell-button"
            >
              {product.upsell.ctaText} (${product.upsell.price})
              <ArrowRight size={18} />
            </button>
          </div>
        )}

        {/* Footer */}
        <div className="text-center mt-12 pt-8 border-t border-white/10">
          <p className="text-gray-400 mb-4">Questions? We're here to help.</p>
          <a href="mailto:support@ramongavea.com" className="text-[#c9a962] hover:underline">
            support@ramongavea.com
          </a>
        </div>
      </div>
    </div>
  );
};

export default SuccessPage;
